import { useEffect, useMemo } from "react";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { invariant } from "@searchui-searchforms/shared/utils";
import type { LocationExactnessScope } from "@/entities/place";
import { LOCATION_EXACTNESS_SCOPE, getPlaceExactnessOptions, useCategoryPlaceDataQuery } from "@/entities/place";
import { AboutTheseSettingsLink, SEARCH_TIPS_PLACES_ANCHOR } from "@/shared/components/AboutTheseSettingsLink";
import { useFormAPI, useFormStore } from "@/shared/forms";
import { usePreviousValue } from "@/shared/hooks/usePreviousValue";
import { useFieldContext } from "../FieldContext";
import { ExactnessButton } from "./ExactnessButton";
import { ExactnessSingleSelect } from "./ExactnessSingleSelect";
import { getExactnessButtonLabelKey, getExactnessSingleSelectLabelKey } from "./place-exactness-utils";

export function PlaceExactness() {
    const { t } = useTranslation();
    const { setFieldValue } = useFormAPI();
    const { fieldPath, exactnessFieldPath } = useFieldContext();

    invariant(exactnessFieldPath, "exactnessFieldPath is required");

    const hasLocation = useFormStore((v) => !!get(v.values, `${fieldPath}.location`));
    const gpid = useFormStore(({ values }) => get(values, `${fieldPath}.gpid`)) as string | null | undefined;
    const exactness = useFormStore(({ values }) => get(values, exactnessFieldPath)) as
        | LocationExactnessScope
        | null
        | undefined;

    const { data } = useCategoryPlaceDataQuery(gpid);
    const place = data?.[0];

    const value = exactness || null;
    const options = useMemo(() => (place ? getPlaceExactnessOptions(place.LevelId, place.IdHierarchy) : []), [place]);

    const translatedOptions = useMemo(
        () =>
            options.map((opt) => ({
                ...opt,
                label: t(opt.labelKey),
                fullLabel: t(opt.fullLabelKey),
            })),
        [options, t],
    );

    const selectedOption = options.find((option) => option.value === value) ?? null;
    const singleSelectLabelKey = getExactnessSingleSelectLabelKey(
        place?.LevelId ?? null,
        selectedOption?.fullLabelKey ?? null,
    );

    const prevGpid = usePreviousValue(gpid);
    useEffect(() => {
        if (prevGpid !== gpid && value) {
            setFieldValue(exactnessFieldPath, LOCATION_EXACTNESS_SCOPE.ThisPlace);
        }
    }, [gpid, prevGpid, value, exactnessFieldPath, setFieldValue]);

    if (!hasLocation) return null;

    if (options.length < 2) {
        return (
            <ExactnessButton
                isExact={!!value}
                onClick={() => {
                    setFieldValue(exactnessFieldPath, value ? null : LOCATION_EXACTNESS_SCOPE.ThisPlace);
                }}
                testId={exactnessFieldPath}
            >
                {t(getExactnessButtonLabelKey(place?.LevelId ?? null))}
            </ExactnessButton>
        );
    }

    return (
        <ExactnessSingleSelect
            value={value}
            options={translatedOptions}
            onChange={(nextValue) => {
                setFieldValue(exactnessFieldPath, nextValue);
            }}
            label={t(singleSelectLabelKey)}
            testId={exactnessFieldPath}
            footer={<AboutTheseSettingsLink anchor={SEARCH_TIPS_PLACES_ANCHOR} />}
        />
    );
}
